import { memo } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Switch } from '@/shared/components/ui/switch';
import { Copy, RefreshCw } from 'lucide-react';
import type { ApiToken } from '../types/apiToken.types';
import { useTranslation } from 'react-i18next';

function formatDateTime(date: string | null): string {
  if (!date) return 'Never';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'Invalid date';
  return parsed.toLocaleString();
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

interface TokenCardProps {
  token: ApiToken;
  hasCachedToken: boolean;
  onCopy: (tokenId: string) => void;
  onRegenerate: (tokenId: string) => void;
  onToggleActive: (tokenId: string, active: boolean) => void;
  isRegenerating: boolean;
  isTogglingActive: boolean;
}

export const TokenCard = memo(function TokenCard({
  token,
  hasCachedToken,
  onCopy,
  onRegenerate,
  onToggleActive,
  isRegenerating,
  isTogglingActive
}: TokenCardProps) {
  const { t } = useTranslation();
  const expired = isExpired(token.expires_at);
  const disabled = !token.is_active || expired;

  return (
    <Card className={disabled ? 'opacity-60' : ''}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{token.token_name}</span>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCopy(token.id)}
              disabled={!token.is_active || expired}
              title={hasCachedToken ? t('workspaces.apiTokens.copyFullToken') : t('workspaces.apiTokens.copyPrefix')}
            >
              <Copy className="h-4 w-4 mr-1" />
              {hasCachedToken ? t('workspaces.apiTokens.copy') : t('workspaces.apiTokens.copyPrefixBtn')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRegenerate(token.id)}
              disabled={isRegenerating}
              title={t('workspaces.apiTokens.regenerateTitle')}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              {t('workspaces.apiTokens.regenerate')}
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {token.is_active && !expired ? t('workspaces.apiTokens.active') : t('workspaces.apiTokens.inactive')}
              </span>
              <Switch
                checked={token.is_active && !expired}
                onCheckedChange={(checked) => onToggleActive(token.id, checked)}
                disabled={isTogglingActive || expired}
              />
            </div>
          </div>
        </CardTitle>
        <CardDescription className="font-mono text-xs">{token.token_prefix}…</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground">{t('workspaces.apiTokens.created')}</div>
            <div>{formatDateTime(token.created_at)}</div>
          </div>
          <div>
            <div className="text-muted-foreground">{t('workspaces.apiTokens.expires')}</div>
            <div>{formatDateTime(token.expires_at)}</div>
          </div>
          <div>
            <div className="text-muted-foreground">{t('workspaces.apiTokens.usageCount')}</div>
            <div>{token.usage_count}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
